/**
 * @name ProdutoImagem
 * @author Keven Adan Bezerra | kevenadanb
 * @description Componente para exibir imagem de produto com fallback para emoji padrão
 * @param {ProdutoImagemProps} props - Props do componente
 * @returns {JSX.Element} Componente de imagem do produto
 * @package src/components/common/ProdutoImagem/ProdutoImagem.tsx
 */

import { getEmojiPorCategoria } from "@/utils/produtoImagemUtils";
import { Box, BoxProps } from "@mui/material";
import React, { useState } from "react";

interface ProdutoImagemProps extends Omit<BoxProps, "children"> {
  /** URL da imagem do produto */
  src?: string | null;
  /** Nome do produto para alt text */
  alt: string;
  /** Categoria do produto para emoji padrão */
  categoria?: string;
  /** ID do produto */
  produtoId?: number;
  /** Se é um combo/pacote */
  isCombo?: boolean;
}

const ProdutoImagem: React.FC<ProdutoImagemProps> = ({
  src,
  alt,
  categoria,
  produtoId,
  isCombo = false,
  sx,
  ...boxProps
}) => {
  const [imagemCarregou, setImagemCarregou] = useState(false);
  const [erroImagem, setErroImagem] = useState(false);

  // Determinar se deve mostrar imagem ou emoji
  const mostrarImagem = src && !erroImagem && imagemCarregou;
  const emojiPadrao = getEmojiPorCategoria(categoria, isCombo);

  const handleImagemCarregada = () => {
    setImagemCarregou(true);
    setErroImagem(false);
  };

  const handleErroImagem = () => {
    setErroImagem(true);
    setImagemCarregou(false);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: { xs: 100, sm: 120, md: 140, lg: 150 },
        borderRadius: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        bgcolor: mostrarImagem ? "white" : "transparent",
        ...sx,
      }}
      {...boxProps}
    >
      {/* Imagem real do produto */}
      {src && !erroImagem && (
        <img
          src={src}
          alt={alt}
          onLoad={handleImagemCarregada}
          onError={handleErroImagem}
          style={{
            width: "calc(100% - 16px)",
            height: "calc(100% - 16px)",
            objectFit: "contain",
            position: "absolute",
            top: "8px",
            left: "8px",
            opacity: imagemCarregou ? 1 : 0,
            transition: "opacity 0.3s ease",
          }}
        />
      )}

      {/* Emoji padrão (sempre presente como fallback) */}
      <Box
        sx={{
          fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem", lg: "3.2rem" },
          opacity: mostrarImagem ? 0 : 1,
          transition: "opacity 0.3s ease",
          position: mostrarImagem ? "absolute" : "static",
          userSelect: "none",
        }}
      >
        {emojiPadrao}
      </Box>

      {/* Loading placeholder durante carregamento da imagem */}
      {src && !imagemCarregou && !erroImagem && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            bgcolor: "grey.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "pulse 1.5s ease-in-out infinite",
          }}
        >
          <Box
            sx={{
              fontSize: {
                xs: "1.2rem",
                sm: "1.5rem",
                md: "1.8rem",
                lg: "2rem",
              },
              opacity: 0.5,
            }}
          >
            {emojiPadrao}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ProdutoImagem;
